import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { lockBodyScroll, unlockBodyScroll } from "../app/bodyScrollLock";
import { itemsViewCopy } from "../app/i18n";
import { getIntlLocale, type AppLanguage } from "../app/language";
import type { AppBranding, ProductPlan, ProductPlanCategory, ProductPlanPrice, ProductPlanPriceInterval, ProductPlanStatus, WorkspaceSettings } from "../app/types";
import { AnimatedModal } from "../app/ui";
import { createRecordId } from "../app/utils";

type ProductPlanDraft = {
  name: string;
  category: ProductPlanCategory;
  status: ProductPlanStatus;
  description: string;
  price: ProductPlanPrice;
  slaEnabled: boolean;
  slaValue: string;
  slaUnit: "Hours" | "Minutes";
  relatedItemIds: string[];
};

type ItemsViewProps = {
  branding: AppBranding;
  workspaceSettings: WorkspaceSettings;
  productPlans: ProductPlan[];
  language: AppLanguage;
  canManageItems: boolean;
  isLoadingItems?: boolean;
  onCreateProductPlan: (productPlan: ProductPlan) => void;
  onDeleteProductPlan: (productPlan: ProductPlan) => void;
  onUpdateProductPlan: (productPlan: ProductPlan) => void;
};

type SortDirection = "asc" | "desc";
type ProductPlanSortColumn = "name" | "status" | "linked" | "category" | "price" | "sla";

const currencyOptions = [
  { label: "USD ($)", code: "USD", symbol: "$" },
  { label: "EUR (€)", code: "EUR", symbol: "€" },
  { label: "GBP (£)", code: "GBP", symbol: "£" },
  { label: "CAD (CA$)", code: "CAD", symbol: "CA$" },
  { label: "JPY (¥)", code: "JPY", symbol: "¥" },
  { label: "AUD (A$)", code: "AUD", symbol: "A$" },
  { label: "NZD (NZ$)", code: "NZD", symbol: "NZ$" },
  { label: "SGD (S$)", code: "SGD", symbol: "S$" },
] as const;

const productPlanCategories: ProductPlanCategory[] = ["Product", "Service", "Subscription"];
const productPlanStatuses: ProductPlanStatus[] = ["Draft", "Public", "Disabled"];
const productPlanPriceIntervals: ProductPlanPriceInterval[] = ["Daily", "Weekly", "Monthly", "Yearly", "One-time"];
const productPlanSlaUnits = ["Hours", "Minutes"] as const;
const productPlanStatusClasses: Record<ProductPlanStatus, string> = {
  Draft: "bg-[#f3f4f6] text-[#475467]",
  Public: "bg-[#effaf5] text-[#027a48]",
  Disabled: "bg-[#fef3f2] text-[#b42318]",
};
const productPlanSortHeaders: ProductPlanSortColumn[] = ["name", "status", "linked", "category", "price", "sla"];
type ItemsCopy = typeof itemsViewCopy.en;

function getEmptyPriceDraft(): ProductPlanPrice {
  return {
    amount: "",
    interval: "Monthly",
  };
}

function getCurrencyMeta(selection: string) {
  return currencyOptions.find(option => option.label === selection) ?? currencyOptions[0];
}

function sanitizePriceAmount(value: string) {
  return value.replace(/[^\d.,-]/g, "").replace(/,/g, "");
}

function hexToRgb(hex: string) {
  const normalized = hex.replace("#", "");
  const safeHex = normalized.length === 3 ? normalized.split("").map(char => `${char}${char}`).join("") : normalized;
  const value = Number.parseInt(safeHex, 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function mixColor(hex: string, amount: number) {
  const { r, g, b } = hexToRgb(hex);
  const target = amount >= 0 ? 255 : 0;
  const intensity = Math.abs(amount);
  const mixChannel = (channel: number) => Math.round(channel + (target - channel) * intensity);

  return `rgb(${mixChannel(r)} ${mixChannel(g)} ${mixChannel(b)})`;
}

function compareText(left: string | null | undefined, right: string | null | undefined) {
  return (left ?? "").localeCompare(right ?? "", undefined, { numeric: true, sensitivity: "base" });
}

function SortIndicatorIcon({ active, direction }: { active: boolean; direction?: SortDirection }) {
  if (!active) {
    return (
      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
        <path d="M3.25 4.25 6 1.5l2.75 2.75" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3.25 7.75 6 10.5l2.75-2.75" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return direction === "asc" ? (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.25 4.5 6 1.75 8.75 4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 1.75v8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  ) : (
    <svg viewBox="0 0 12 12" fill="none" aria-hidden="true" className="h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg">
      <path d="M3.25 7.5 6 10.25 8.75 7.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M6 1.75v8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function getSortIndicator(activeColumn: string, activeDirection: SortDirection, column: string) {
  const isActive = activeColumn === column;

  return <SortIndicatorIcon active={isActive} direction={isActive ? activeDirection : undefined} />;
}

function formatPriceAmount(amount: string, currencySelection: string, language: AppLanguage, copy: ItemsCopy) {
  const normalizedAmount = sanitizePriceAmount(amount).trim();

  if (!normalizedAmount) {
    return copy.pricing.emptyAmount;
  }

  const numericAmount = Number(normalizedAmount);
  const { code, symbol } = getCurrencyMeta(currencySelection);

  if (!Number.isFinite(numericAmount)) {
    return `${symbol}${normalizedAmount}`;
  }

  return new Intl.NumberFormat(getIntlLocale(language), {
    style: "currency",
    currency: code,
    minimumFractionDigits: Number.isInteger(numericAmount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

function parseProductPlanSla(sla: string | null | undefined): { enabled: boolean; value: string; unit: "Hours" | "Minutes" } {
  const normalizedSla = sla?.trim() ?? "";

  if (!normalizedSla) {
    return {
      enabled: false,
      value: "",
      unit: "Hours",
    };
  }

  const matchedSla = normalizedSla.match(/(\d+(?:\.\d+)?)\s*(hour|hours|hr|hrs|minute|minutes|min|mins)/i);

  if (!matchedSla) {
    return {
      enabled: true,
      value: normalizedSla.match(/\d+(?:\.\d+)?/)?.[0] ?? "",
      unit: "Hours",
    };
  }

  const [, value = "", unitLabel = ""] = matchedSla;

  return {
    enabled: true,
    value,
    unit: /min/i.test(unitLabel) ? "Minutes" : "Hours",
  };
}

function formatProductPlanSla(value: string, unit: "Hours" | "Minutes", copy: ItemsCopy) {
  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return null;
  }

  const numericValue = Number(normalizedValue);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return null;
  }

  const normalizedUnit = unit === "Minutes"
    ? numericValue === 1 ? copy.sla.minute : copy.sla.minutes
    : numericValue === 1 ? copy.sla.hour : copy.sla.hours;

  return `${normalizedValue} ${normalizedUnit}`;
}

function getProductPlanSlaDisplayLabel(sla: string | null | undefined, copy: ItemsCopy) {
  const parsedSla = parseProductPlanSla(sla);

  if (!parsedSla.enabled) {
    return copy.sla.disabled;
  }

  return formatProductPlanSla(parsedSla.value, parsedSla.unit, copy) ?? sla?.trim() ?? copy.sla.disabled;
}

function getEmptyProductPlanDraft(): ProductPlanDraft {
  return {
    name: "",
    category: "Subscription",
    status: "Draft",
    description: "",
    price: getEmptyPriceDraft(),
    slaEnabled: false,
    slaValue: "",
    slaUnit: "Hours",
    relatedItemIds: [],
  };
}

function getProductPlanDraft(plan: ProductPlan): ProductPlanDraft {
  const parsedSla = parseProductPlanSla(plan.sla);

  return {
    name: plan.name,
    category: plan.category,
    status: plan.status,
    description: plan.description,
    price: { ...plan.price },
    slaEnabled: parsedSla.enabled,
    slaValue: parsedSla.value,
    slaUnit: parsedSla.unit,
    relatedItemIds: [...plan.relatedItemIds],
  };
}

function getProductPlanFromDraft(draft: ProductPlanDraft, id: string, copy: ItemsCopy): ProductPlan {
  const normalizedSla = draft.slaEnabled ? formatProductPlanSla(draft.slaValue, draft.slaUnit, copy) : null;

  return {
    id,
    name: draft.name.trim(),
    category: draft.category,
    status: draft.status,
    description: draft.description.trim(),
    price: {
      amount: sanitizePriceAmount(draft.price.amount.trim()),
      interval: draft.price.interval,
    },
    sla: normalizedSla,
    relatedItemIds: [...new Set(draft.relatedItemIds)],
  };
}

function SettingsCard({
  eyebrow,
  title,
  description,
  actions,
  children,
}: {
  eyebrow: string;
  title: string;
  description: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-[28px] bg-white p-7 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{eyebrow}</p>
          <h2 className="mt-3 font-headline text-2xl font-extrabold tracking-[-0.04em] text-[#101828]">{title}</h2>
          <p className="mt-2 max-w-2xl text-sm text-[#667085]">{description}</p>
        </div>
        {actions ?? null}
      </div>
      {children}
    </section>
  );
}

function CountSpinner() {
  return (
    <span className="inline-flex h-9 w-9 items-center justify-center text-[var(--brand-primary)]" aria-label="Loading">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-7 w-7 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function MetricCard({ label, value, detail, isLoading = false }: { label: string; value: string; detail: string; isLoading?: boolean }) {
  return (
    <div className="rounded-[24px] bg-white p-6 shadow-[0_20px_60px_rgba(15,23,42,0.06)]">
      <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#98a2b3]">{label}</p>
      <div className="mt-4 min-h-[2.25rem]">
        {isLoading ? <CountSpinner /> : <p className="text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">{value}</p>}
      </div>
      <p className="mt-2 text-sm text-[#667085]">{detail}</p>
    </div>
  );
}

function LoadingSpinnerBadge({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-[var(--brand-primary-ring)] bg-white/95 px-3 py-2 text-xs font-semibold text-[var(--brand-primary)] shadow-[0_10px_24px_rgba(15,23,42,0.08)]">
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg">
        <circle cx="10" cy="10" r="7" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
        <path d="M10 3a7 7 0 0 1 7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <span>{label}</span>
    </div>
  );
}

export function ItemsView({
  branding,
  workspaceSettings,
  productPlans,
  language,
  canManageItems,
  isLoadingItems = false,
  onCreateProductPlan,
  onDeleteProductPlan,
  onUpdateProductPlan,
}: ItemsViewProps) {
  const copy = itemsViewCopy[language];
  const brandRgb = hexToRgb(branding.primaryColor);
  const selectedCurrency = getCurrencyMeta(workspaceSettings.currency);
  const portalBrandStyle = {
    "--brand-primary": branding.primaryColor,
    "--brand-primary-strong": mixColor(branding.primaryColor, -0.14),
    "--brand-primary-soft": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.1)`,
    "--brand-primary-shadow": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.2)`,
    "--brand-primary-shadow-strong": `rgba(${brandRgb.r}, ${brandRgb.g}, ${brandRgb.b}, 0.3)`,
  } as CSSProperties;
  const [selectedProductPlanId, setSelectedProductPlanId] = useState<string | null>(productPlans[0]?.id ?? null);
  const [isCreatingProductPlan, setIsCreatingProductPlan] = useState(false);
  const [isProductPlanModalOpen, setIsProductPlanModalOpen] = useState(false);
  const [openLinkedPopoverId, setOpenLinkedPopoverId] = useState<string | null>(null);
  const [productPlanDraft, setProductPlanDraft] = useState<ProductPlanDraft>(() =>
    productPlans[0] ? getProductPlanDraft(productPlans[0]) : getEmptyProductPlanDraft(),
  );
  const [productPlanError, setProductPlanError] = useState("");
  const [productPlanSavedState, setProductPlanSavedState] = useState(copy.page.syncedState);
  const [productPlanSortColumn, setProductPlanSortColumn] = useState<ProductPlanSortColumn>("name");
  const [productPlanSortDirection, setProductPlanSortDirection] = useState<SortDirection>("asc");

  useEffect(() => {
    setProductPlanSavedState(current => {
      if (current === itemsViewCopy.en.page.syncedState || current === itemsViewCopy.ja.page.syncedState) {
        return copy.page.syncedState;
      }

      return current;
    });
  }, [copy.page.syncedState]);

  const selectableRelatedItems = productPlans.filter(plan => plan.id !== selectedProductPlanId);
  const getRelatedItemNames = (itemIds: string[]) =>
    itemIds
      .map(itemId => productPlans.find(plan => plan.id === itemId)?.name)
      .filter((name): name is string => Boolean(name));

  const sortedProductPlans = useMemo(() => {
    const directionMultiplier = productPlanSortDirection === "asc" ? 1 : -1;

    return [...productPlans].sort((left, right) => {
      let comparison = 0;

      switch (productPlanSortColumn) {
        case "name":
          comparison = compareText(left.name, right.name);
          break;
        case "status":
          comparison = productPlanStatuses.indexOf(left.status) - productPlanStatuses.indexOf(right.status);
          break;
        case "linked":
          comparison = left.relatedItemIds.length - right.relatedItemIds.length;
          break;
        case "category":
          comparison = compareText(left.category, right.category);
          break;
        case "price":
          comparison = Number(left.price.amount || 0) - Number(right.price.amount || 0);
          break;
        case "sla":
          comparison = compareText(left.sla, right.sla);
          break;
      }

      if (comparison === 0) {
        comparison = compareText(left.name, right.name);
      }

      return comparison * directionMultiplier;
    });
  }, [productPlanSortColumn, productPlanSortDirection, productPlans]);

  useEffect(() => {
    if (isCreatingProductPlan) {
      return;
    }

    const selectedPlan = productPlans.find(plan => plan.id === selectedProductPlanId) ?? productPlans[0];

    if (!selectedPlan) {
      setSelectedProductPlanId(null);
      setProductPlanDraft(getEmptyProductPlanDraft());
      return;
    }

    if (selectedPlan.id !== selectedProductPlanId) {
      setSelectedProductPlanId(selectedPlan.id);
    }

    setProductPlanDraft(getProductPlanDraft(selectedPlan));
  }, [isCreatingProductPlan, productPlans, selectedProductPlanId]);

  useEffect(() => {
    if (!openLinkedPopoverId || typeof document === "undefined") {
      return;
    }

    const handlePointerDown = () => {
      setOpenLinkedPopoverId(null);
    };

    document.addEventListener("mousedown", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [openLinkedPopoverId]);

  useEffect(() => {
    if (!isProductPlanModalOpen || typeof document === "undefined") {
      return;
    }

    lockBodyScroll();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleCloseProductPlanModal();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      unlockBodyScroll();
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isProductPlanModalOpen, isCreatingProductPlan, productPlans, selectedProductPlanId]);

  const handleProductPlanSort = (column: ProductPlanSortColumn) => {
    if (productPlanSortColumn === column) {
      setProductPlanSortDirection(current => (current === "asc" ? "desc" : "asc"));
      return;
    }

    setProductPlanSortColumn(column);
    setProductPlanSortDirection(column === "price" || column === "linked" ? "desc" : "asc");
  };

  const handleProductPlanDraftChange = <K extends keyof ProductPlanDraft>(field: K, value: ProductPlanDraft[K]) => {
    setProductPlanDraft(current => ({ ...current, [field]: value }));
  };

  const handleProductPlanPriceChange = <K extends keyof ProductPlanPrice>(field: K, value: ProductPlanPrice[K]) => {
    setProductPlanDraft(current => ({
      ...current,
      price: { ...current.price, [field]: value },
    }));
  };

  const handleToggleRelatedItem = (itemId: string) => {
    setProductPlanDraft(current => ({
      ...current,
      relatedItemIds: current.relatedItemIds.includes(itemId)
        ? current.relatedItemIds.filter(id => id !== itemId)
        : [...current.relatedItemIds, itemId],
    }));
  };

  const handleEditProductPlan = (plan: ProductPlan) => {
    setIsCreatingProductPlan(false);
    setSelectedProductPlanId(plan.id);
    setProductPlanDraft(getProductPlanDraft(plan));
    setProductPlanError("");
    setIsProductPlanModalOpen(true);
  };

  const handleCreateProductPlan = () => {
    setIsCreatingProductPlan(true);
    setSelectedProductPlanId(null);
    setProductPlanDraft(getEmptyProductPlanDraft());
    setProductPlanError("");
    setIsProductPlanModalOpen(true);
  };

  const handleCloseProductPlanModal = () => {
    setIsProductPlanModalOpen(false);
    setProductPlanError("");

    if (isCreatingProductPlan) {
      setIsCreatingProductPlan(false);

      const fallbackPlan = productPlans.find(plan => plan.id === selectedProductPlanId) ?? productPlans[0];

      if (fallbackPlan) {
        setSelectedProductPlanId(fallbackPlan.id);
        setProductPlanDraft(getProductPlanDraft(fallbackPlan));
      } else {
        setProductPlanDraft(getEmptyProductPlanDraft());
      }

      return;
    }

    const selectedPlan = productPlans.find(plan => plan.id === selectedProductPlanId);

    if (selectedPlan) {
      setProductPlanDraft(getProductPlanDraft(selectedPlan));
    }
  };

  const handleResetProductPlanDraft = () => {
    if (isCreatingProductPlan) {
      setProductPlanDraft(getEmptyProductPlanDraft());
      setProductPlanError("");
      return;
    }

    const selectedPlan = productPlans.find(plan => plan.id === selectedProductPlanId);

    if (!selectedPlan) {
      return;
    }

    setProductPlanDraft(getProductPlanDraft(selectedPlan));
    setProductPlanError("");
  };

  const handleSaveProductPlan = () => {
    if (!productPlanDraft.name.trim()) {
      setProductPlanError(copy.errors.missingName);
      return;
    }

    if (!productPlanDraft.price.amount.trim()) {
      setProductPlanError(copy.errors.missingPrice);
      return;
    }

    if (productPlanDraft.slaEnabled) {
      const numericSlaValue = Number(productPlanDraft.slaValue.trim());

      if (!Number.isFinite(numericSlaValue) || numericSlaValue <= 0) {
        setProductPlanError(copy.errors.invalidPositiveSla);
        return;
      }
    }

    if (productPlanDraft.slaEnabled && !formatProductPlanSla(productPlanDraft.slaValue, productPlanDraft.slaUnit, copy)) {
      setProductPlanError(copy.errors.invalidSla);
      return;
    }

    const nextProductPlan = getProductPlanFromDraft(
      productPlanDraft,
      isCreatingProductPlan ? createRecordId("plan") : selectedProductPlanId ?? createRecordId("plan"),
      copy,
    );

    if (isCreatingProductPlan) {
      onCreateProductPlan(nextProductPlan);
      setIsCreatingProductPlan(false);
      setSelectedProductPlanId(nextProductPlan.id);
      setProductPlanSavedState(copy.page.createdState);
    } else {
      onUpdateProductPlan(nextProductPlan);
      setProductPlanSavedState(copy.page.updatedState(nextProductPlan.name));
    }

    setProductPlanDraft(getProductPlanDraft(nextProductPlan));
    setProductPlanError("");
    setIsProductPlanModalOpen(false);
  };

  const pricedItemsCount = productPlans.filter(plan => plan.price.amount).length;
  const liveItemsCount = productPlans.filter(plan => plan.status === "Public").length;
  const slaItemsCount = productPlans.filter(plan => Boolean(plan.sla)).length;
  const linkedBundlesCount = productPlans.filter(plan => plan.relatedItemIds.length > 0).length;

  return (
    <section className="space-y-8 p-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[#98a2b3]">{copy.page.eyebrow}</p>
          <h1 className="mt-3 font-headline text-5xl font-extrabold tracking-[-0.06em] text-[#101828]">{copy.page.title}</h1>
          <p className="mt-3 max-w-3xl text-sm text-[#667085]">{copy.page.description}</p>
        </div>

        <div className="rounded-[24px] bg-white px-5 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.06)]">
          <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{copy.page.catalogState}</p>
          <div className="mt-2 flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-[#12b76a]" />
            <span className="text-sm font-semibold text-[#101828]">{productPlanSavedState}</span>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label={copy.metrics.trackedItems} value={String(productPlans.length).padStart(2, "0")} detail={copy.metrics.trackedItemsDetail} isLoading={isLoadingItems} />
        <MetricCard label={copy.metrics.publicItems} value={String(liveItemsCount).padStart(2, "0")} detail={copy.metrics.publicItemsDetail} isLoading={isLoadingItems} />
        <MetricCard label={copy.metrics.savedPrices} value={String(pricedItemsCount).padStart(2, "0")} detail={copy.metrics.savedPricesDetail} isLoading={isLoadingItems} />
        <MetricCard label={copy.metrics.slaCoverage} value={String(slaItemsCount).padStart(2, "0")} detail={copy.metrics.slaCoverageDetail(linkedBundlesCount)} isLoading={isLoadingItems} />
      </div>

      <SettingsCard
        eyebrow={copy.library.eyebrow}
        title={copy.library.title}
        description={copy.library.description}
        actions={
          <div className="ml-auto flex w-full flex-1 items-center justify-end gap-3 self-start">
            {canManageItems ? (
              <button
                type="button"
                onClick={handleCreateProductPlan}
                className="brand-primary-button rounded-xl px-4 py-2.5 text-sm font-bold transition-all"
              >
                {copy.library.newItem}
              </button>
            ) : null}
          </div>
        }
      >
        <div className="relative overflow-visible rounded-[26px]">
          {isLoadingItems ? (
            <div className={`pointer-events-none absolute inset-x-0 z-[2] flex ${sortedProductPlans.length === 0 ? "inset-y-0 items-center justify-center rounded-[26px] bg-white/75 backdrop-blur-[1px]" : "bottom-5 items-center justify-center px-5"}`}>
              <LoadingSpinnerBadge label={copy.library.loading} />
            </div>
          ) : null}
          <div className="overflow-hidden rounded-[26px] border border-[#eaecf0] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.04)]">
            <div className="overflow-x-auto">
              <div className={`min-w-[900px] ${sortedProductPlans.length === 0 ? "min-h-[220px]" : ""}`}>
                <div className="settings-table-head hidden grid-cols-[200px_96px_92px_96px_120px_120px_136px] gap-3 bg-[#f9fafb] px-5 py-4 lg:grid">
                  {productPlanSortHeaders.map(header => (
                    <button
                      key={header}
                      type="button"
                      onClick={() => handleProductPlanSort(header)}
                      className="flex items-center gap-2 text-left text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3] transition-colors hover:text-[#667085]"
                      aria-label={copy.table.sortAriaLabel(copy.table.headers[header], productPlanSortColumn === header ? productPlanSortDirection : undefined)}
                    >
                      <span>{copy.table.headers[header]}</span>
                      <span className={`inline-flex items-center ${productPlanSortColumn === header ? "text-[#667085]" : "text-slate-300"}`} aria-hidden="true">
                        {getSortIndicator(productPlanSortColumn, productPlanSortDirection, header)}
                      </span>
                    </button>
                  ))}
                  <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.table.headers.action}</span>
                </div>

                <div className="settings-table-body">
                  {sortedProductPlans.length === 0 ? (
                    isLoadingItems ? null : (
                      <div className="px-5 py-6">
                        <div className="rounded-[24px] border border-dashed border-[#d0d5dd] bg-[#fcfcfd] px-6 py-12 text-center">
                          <p className="text-sm font-semibold text-[#101828]">{copy.library.emptyTitle}</p>
                          <p className="mt-2 text-sm text-[#667085]">{copy.library.emptyDetail}</p>
                        </div>
                      </div>
                    )
                  ) : sortedProductPlans.map(plan => {
                    const relatedItemNames = getRelatedItemNames(plan.relatedItemIds);

                    return (
                      <div
                        key={plan.id}
                        className="settings-table-row grid gap-3 bg-white px-5 py-5 transition-colors hover:bg-[var(--brand-primary-soft)] lg:grid-cols-[200px_96px_92px_96px_120px_120px_136px]"
                      >
                        <div className="min-w-0">
                          <h3 className="font-headline text-xl font-extrabold tracking-[-0.04em] text-[#101828]">{plan.name}</h3>
                        </div>

                        <div>
                          <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold ${productPlanStatusClasses[plan.status]}`}>{copy.statusLabels[plan.status]}</span>
                        </div>

                        <div className="relative">
                          <button
                            type="button"
                            onClick={event => {
                              event.stopPropagation();
                              setOpenLinkedPopoverId(current => (current === plan.id ? null : plan.id));
                            }}
                            className="inline-flex rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467] transition-colors hover:bg-[#e5e7eb]"
                          >
                            {relatedItemNames.length > 0 ? copy.table.linkedCount(relatedItemNames.length) : copy.table.none}
                          </button>

                          {openLinkedPopoverId === plan.id && relatedItemNames.length > 0 ? (
                            <div
                              className="absolute left-0 top-full z-20 mt-2 min-w-[11rem] rounded-2xl border border-[#eaecf0] bg-white p-2 shadow-[0_24px_48px_rgba(15,23,42,0.14)]"
                              onClick={event => event.stopPropagation()}
                            >
                              <p className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#98a2b3]">{copy.table.linkedItems}</p>
                              <div className="space-y-1">
                                {relatedItemNames.map(name => (
                                  <div key={name} className="rounded-xl bg-[#f9fafb] px-3 py-2 text-sm font-medium text-[#475467]">
                                    {name}
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : null}
                        </div>

                        <div>
                          <span className="inline-flex rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">{copy.categoryLabels[plan.category]}</span>
                        </div>

                        <div>
                          <p className="text-sm font-semibold text-[#101828]">{formatPriceAmount(plan.price.amount, selectedCurrency.label, language, copy)}</p>
                          <p className="mt-1 text-xs text-[#667085]">{copy.intervalLabels[plan.price.interval]}</p>
                        </div>

                        <div>
                          <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold ${plan.sla ? "bg-[#eff8ff] text-[#175cd3]" : "bg-[#f3f4f6] text-[#475467]"}`}>
                            {getProductPlanSlaDisplayLabel(plan.sla, copy)}
                          </span>
                        </div>

                        <div className="flex items-start gap-2">
                          {canManageItems ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleEditProductPlan(plan)}
                                className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                              >
                                {copy.table.edit}
                              </button>
                              <button
                                type="button"
                                onClick={() => onDeleteProductPlan(plan)}
                                className="rounded-xl bg-white px-3 py-2 text-sm font-semibold text-[#b42318] ring-1 ring-[#f3d0cb] transition-colors hover:bg-[#fef3f2]"
                              >
                                {copy.table.delete}
                              </button>
                            </>
                          ) : <span className="rounded-full bg-[#f3f4f6] px-3 py-1 text-[11px] font-semibold text-[#475467]">{copy.library.readOnly}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SettingsCard>

      <AnimatedModal
        open={canManageItems && isProductPlanModalOpen}
        onClose={handleCloseProductPlanModal}
        backdropAriaLabel={copy.modal.closeDialog}
        overlayClassName="fixed inset-0 z-[90] bg-[#101828]/35 backdrop-blur-[6px]"
        overlayStyle={portalBrandStyle}
        dialogClassName="app-modal-dialog relative z-10 max-h-[calc(100vh-4rem)] w-full max-w-4xl overflow-y-auto rounded-[32px] border border-white/70 bg-[#fcfcfd] p-6 shadow-[0_32px_100px_rgba(15,23,42,0.24)] custom-scrollbar"
      >
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#98a2b3]">{isCreatingProductPlan ? copy.modal.createEyebrow : copy.modal.editEyebrow}</p>
                      <h3 className="mt-2 font-headline text-3xl font-extrabold tracking-[-0.04em] text-[#101828]">
                        {isCreatingProductPlan ? copy.modal.createTitle : productPlanDraft.name || copy.modal.editFallbackTitle}
                      </h3>
                      <p className="mt-2 max-w-2xl text-sm text-[#667085]">{copy.modal.description}</p>
                    </div>

                    <button
                      type="button"
                      onClick={handleCloseProductPlanModal}
                      className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-lg font-semibold text-slate-500 shadow-[0_12px_24px_rgba(15,23,42,0.08)] transition-colors hover:bg-[#f3f4f6] hover:text-[#1a1c1d]"
                    >
                      ×
                    </button>
                  </div>

                  <div className="mt-6 grid gap-4 md:grid-cols-3">
                    <label className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.modal.itemName}</span>
                      <input
                        type="text"
                        value={productPlanDraft.name}
                        onChange={event => handleProductPlanDraftChange("name", event.target.value)}
                        placeholder={copy.modal.itemNamePlaceholder}
                        className="mt-3 w-full border-none bg-transparent p-0 text-sm font-semibold text-[#101828] outline-none placeholder:text-[#98a2b3]"
                      />
                    </label>

                    <label className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.modal.itemType}</span>
                      <select
                        value={productPlanDraft.category}
                        onChange={event => handleProductPlanDraftChange("category", event.target.value as ProductPlanCategory)}
                        className="mt-3 w-full border-none bg-transparent p-0 text-sm font-semibold text-[#101828] outline-none"
                      >
                        {productPlanCategories.map(category => (
                          <option key={category} value={category}>
                            {copy.categoryLabels[category]}
                          </option>
                        ))}
                      </select>
                    </label>

                    <label className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.modal.status}</span>
                      <select
                        value={productPlanDraft.status}
                        onChange={event => handleProductPlanDraftChange("status", event.target.value as ProductPlanStatus)}
                        className="mt-3 w-full border-none bg-transparent p-0 text-sm font-semibold text-[#101828] outline-none"
                      >
                        {productPlanStatuses.map(status => (
                          <option key={status} value={status}>
                            {copy.statusLabels[status]}
                          </option>
                        ))}
                      </select>
                    </label>

                    <div className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4 md:col-span-3">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.modal.pricing}</span>
                          <p className="mt-2 text-sm text-[#667085]">{copy.modal.pricingDescription(selectedCurrency.label)}</p>
                        </div>
                      </div>

                      <div className="mt-4 grid gap-3 md:grid-cols-[minmax(140px,0.6fr)_minmax(0,1fr)]">
                        <div className="flex items-center rounded-xl border border-[#eaecf0] bg-white px-3 py-2">
                          <span className="mr-2 text-sm font-semibold text-[#667085]">{selectedCurrency.symbol}</span>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={productPlanDraft.price.amount}
                            onChange={event => handleProductPlanPriceChange("amount", sanitizePriceAmount(event.target.value))}
                            placeholder="99"
                            className="w-full border-none bg-transparent p-0 text-sm font-semibold text-[#101828] outline-none placeholder:text-[#98a2b3]"
                          />
                        </div>
                        <select
                          value={productPlanDraft.price.interval}
                          onChange={event => handleProductPlanPriceChange("interval", event.target.value as ProductPlanPriceInterval)}
                          className="rounded-xl border border-[#eaecf0] bg-white px-3 py-2 text-sm font-medium text-[#101828] outline-none"
                        >
                          {productPlanPriceIntervals.map(interval => (
                            <option key={interval} value={interval}>
                              {copy.intervalLabels[interval]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4 md:col-span-3">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.modal.ticketSla}</span>
                          <p className="mt-2 text-sm text-[#667085]">{copy.modal.ticketSlaDescription}</p>
                        </div>

                        <label className="inline-flex items-center gap-2 rounded-full bg-[#f3f4f6] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#475467]">
                          <input
                            type="checkbox"
                            checked={productPlanDraft.slaEnabled}
                            onChange={event => handleProductPlanDraftChange("slaEnabled", event.target.checked)}
                            className="h-4 w-4 rounded border-[#d0d5dd]"
                            style={{ accentColor: "var(--brand-primary)" }}
                          />
                          {copy.modal.enabled}
                        </label>
                      </div>

                      {productPlanDraft.slaEnabled ? (
                        <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,0.7fr)_minmax(180px,0.3fr)]">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            inputMode="numeric"
                            value={productPlanDraft.slaValue}
                            onChange={event => handleProductPlanDraftChange("slaValue", event.target.value)}
                            placeholder="2"
                            className="w-full rounded-xl border border-[#eaecf0] bg-white px-4 py-3 text-sm font-medium text-[#101828] outline-none placeholder:text-[#98a2b3]"
                          />
                          <select
                            value={productPlanDraft.slaUnit}
                            onChange={event => handleProductPlanDraftChange("slaUnit", event.target.value as "Hours" | "Minutes")}
                            className="rounded-xl border border-[#eaecf0] bg-white px-3 py-3 text-sm font-medium text-[#101828] outline-none"
                          >
                            {productPlanSlaUnits.map(unit => (
                              <option key={unit} value={unit}>
                                {copy.slaUnitLabels[unit]}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <p className="mt-4 text-sm text-[#98a2b3]">{copy.modal.disabledHint}</p>
                      )}
                    </div>

                    <div className="rounded-[22px] border border-[#eaecf0] bg-white px-5 py-4 md:col-span-3">
                      <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#98a2b3]">{copy.modal.linkedItems}</span>
                      <p className="mt-2 text-sm text-[#667085]">{copy.modal.linkedItemsDescription}</p>

                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        {selectableRelatedItems.length > 0 ? (
                          selectableRelatedItems.map(plan => {
                            const checked = productPlanDraft.relatedItemIds.includes(plan.id);
                            const priceSummary = plan.price.amount
                              ? `${formatPriceAmount(plan.price.amount, selectedCurrency.label, language, copy)} · ${copy.intervalLabels[plan.price.interval]}`
                              : copy.table.noPrice;

                            return (
                              <label key={plan.id} className={`flex items-start gap-3 rounded-[18px] border px-4 py-3 transition-colors ${checked ? "border-[var(--brand-primary)] bg-[var(--brand-primary-soft)]" : "border-[#eaecf0] bg-[#fcfcfd]"}`}>
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => handleToggleRelatedItem(plan.id)}
                                  className="mt-1 h-4 w-4 rounded border-[#d0d5dd]"
                                  style={{ accentColor: "var(--brand-primary)" }}
                                />
                                <span>
                                  <span className="block text-sm font-semibold text-[#101828]">{plan.name}</span>
                                  <span className="mt-1 block text-xs text-[#667085]">{copy.categoryLabels[plan.category]} · {copy.statusLabels[plan.status]}</span>
                                  <span className="mt-1 block text-xs font-medium text-[#475467]">{priceSummary}</span>
                                </span>
                              </label>
                            );
                          })
                        ) : (
                          <p className="text-sm text-[#98a2b3]">{copy.modal.createAnotherFirst}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {productPlanError ? <p className="mt-4 text-sm font-semibold text-[#b42318]">{productPlanError}</p> : null}

                  <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={handleResetProductPlanDraft}
                      className="rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-[#344054] ring-1 ring-[#d0d5dd] transition-colors hover:bg-[#f9fafb]"
                    >
                      {isCreatingProductPlan ? copy.modal.clearForm : copy.modal.resetChanges}
                    </button>

                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={handleCloseProductPlanModal}
                        className="rounded-xl px-4 py-2.5 text-sm font-semibold text-[#475467] transition-colors hover:bg-[#f3f4f6]"
                      >
                        {copy.modal.cancel}
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveProductPlan}
                        className="brand-primary-button rounded-xl px-5 py-2.5 text-sm font-bold transition-all"
                      >
                        {isCreatingProductPlan ? copy.modal.createItem : copy.modal.saveItem}
                      </button>
                    </div>
                  </div>
      </AnimatedModal>
    </section>
  );
}
